import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SensorType } from '@app/sensordaten/models/sensor-type.model';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ApiService } from '@app/shared/services/api/api.service';
import { chain, isNil } from 'lodash';
import { DeviceCategory, EnergyTypesName, SensorTypes } from '@app/shared/enums';
import { DeviceTypeName, DeviceTypes } from '../models/device-types';
import { defaultSensorTypeIcon } from '@app/shared/constants';
import { StringUtility } from '@app/shared/string-utility';
import { ActiveSensor } from '../models/active-sensor';

@Injectable({
  providedIn: 'root',
})
export class SensorTypesService extends ApiService {
  private sensorTypes$?: Observable<SensorType[]>;

  constructor(public http: HttpClient) {
    super(http);
  }

  public getSensorTypes(): Observable<SensorType[]> {
    if (!this.sensorTypes$) {
      this.sensorTypes$ = this.http
        .get<DeviceTypes[]>(this.endpoint(`socrates/administration/api/references/devicetypes`))
        .pipe(this.mapSensorType$, shareReplay({ bufferSize: 1, refCount: true }));
    }
    return this.sensorTypes$;
  }

  private mapSensorType$ = (response: Observable<DeviceTypes[]>): Observable<SensorType[]> =>
    response.pipe(
      map((query: DeviceTypes[]): SensorType[] =>
        chain(query)
          .filter((type: DeviceTypes) => type.typeName !== '')
          .orderBy((type: DeviceTypes) => type.displayName)
          .map((type: DeviceTypes) => ({
            code: type.typeName,
            name: this.setDisplayName(type),
            image: isNil(type.iconName) ? defaultSensorTypeIcon : `ico_${type.iconName}.svg`,
            typeName: type.displayName,
          }))
          .value()
      )
    );

  public refreshDeviceTypes(): Observable<SensorType[]> {
    this.sensorTypes$ = this.http
      .get<DeviceTypes[]>(this.endpoint(`socrates/administration/api/references/devicetypes`))
      .pipe(this.mapSensorType$, shareReplay({ bufferSize: 1, refCount: true }));
    return this.sensorTypes$;
  }

  private setDisplayName(type: DeviceTypes): string {
    let displayName = type.displayName;
    if (StringUtility.areStringsIdentical(type.category, DeviceCategory.meter)) {
      displayName = StringUtility.areStringsIdentical(type.typeName, SensorTypes.ELECTRICITY) ? EnergyTypesName.electricity : EnergyTypesName.gas;
    }
    return displayName;
  }

  public getSensorTypesBy(municipalityKey: string): Observable<ActiveSensor[]> {
    const sensorDataOutOfScopeSensors = [SensorTypes.ELECTRICITY, SensorTypes.GAS].map((sensor: SensorTypes) => `'${sensor}'`).join();
    // todo Franz :: Raise inconsistencies to Socrates => can select typeName but /devices endpoint return is of deviceTypeName
    return this.http
      .get<DeviceTypeName[]>(
        this.endpoint(
          `socrates/administration/api/municipalities/${municipalityKey}/devices?$filter=isActive and typeName in (${sensorDataOutOfScopeSensors}) eq false&$select=deviceId,typeName,onBoardingDate,onboardingKey`
        )
      )
      .pipe(this.mapDeviceTypes$);
  }

  private mapDeviceTypes$ = (response: Observable<DeviceTypeName[]>): Observable<ActiveSensor[]> =>
    response.pipe(
      map((query: DeviceTypeName[]): ActiveSensor[] =>
        chain(query)
          .filter((device: DeviceTypeName) => device.deviceTypeName !== '')
          .orderBy((device: DeviceTypeName) => device.deviceTypeName)
          .map((device: DeviceTypeName) => ({
            code: device.deviceTypeName,
          }))
          .value()
      )
    );
}
